import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['primaryButton', 'secondaryButton', 'form', 'learnMore', 'submitButton', 'waitlistForm', 'messages', 'emailInput']

  declare readonly primaryButtonTarget: HTMLElement
  declare readonly secondaryButtonTarget: HTMLElement
  declare readonly formTarget: HTMLElement
  declare readonly learnMoreTarget: HTMLElement
  declare readonly submitButtonTarget: HTMLButtonElement
  declare readonly waitlistFormTarget: HTMLFormElement
  declare readonly messagesTarget: HTMLElement
  declare readonly emailInputTarget: HTMLInputElement

  private isSubmitting: boolean = false

  toggleForm(event: Event) {
    event.preventDefault()
    // Toggle UI: hide CTAs, reveal inline form and helper link
    if (this.primaryButtonTarget) this.primaryButtonTarget.classList.add('hidden')
    if (this.secondaryButtonTarget) this.secondaryButtonTarget.classList.add('hidden')
    if (this.formTarget) this.formTarget.classList.remove('hidden')
    if (this.learnMoreTarget) this.learnMoreTarget.classList.remove('hidden')

    // Set focus to the email input inside the waitlist form component
    const input = this.formTarget.querySelector('input[type="email"]') as HTMLInputElement
    if (input) setTimeout(() => input.focus(), 100)
  }

  scrollToHow(event: Event) {
    event.preventDefault()
    const target = document.querySelector('#how-it-works')
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  handleSuccess() {
    // Keep the form visible for a few seconds to show success message, then collapse
    setTimeout(() => {
      if (this.formTarget) this.formTarget.classList.add('hidden')
      if (this.primaryButtonTarget) this.primaryButtonTarget.classList.remove('hidden')
      if (this.secondaryButtonTarget) this.secondaryButtonTarget.classList.remove('hidden')
      if (this.learnMoreTarget) this.learnMoreTarget.classList.add('hidden')
    }, 3000)

    // return focus to email input for keyboard users
    const input = this.formTarget.querySelector('input[type="email"]') as HTMLInputElement
    if (input) setTimeout(() => input.focus(), 50)
  }

  handleSubmitStart(event: Event) {
    if (this.isSubmitting) {
      event.preventDefault()
      return
    }

    this.isSubmitting = true
    if (this.submitButtonTarget) {
      this.submitButtonTarget.disabled = true
      // preserve localised string when possible
      this.submitButtonTarget.textContent = 'Enviando...'
    }
    this.clearMessages()
  }

  // turbo:submit-end detail contains { success, fetchResponse }
  async handleSubmitEnd(event: Event) {
    this.isSubmitting = false
    // re-enable button regardless
    if (this.submitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = 'Unirme a la lista de espera'
    }
    // @ts-ignore - Turbo passes a CustomEvent with detail
    const detail = (event as CustomEvent).detail || {}
    const success = Boolean(detail.success)
    const fetchResponse = detail.fetchResponse

    // Helper to extract JSON/body safely from Turbo's fetchResponse
    const extractJson = async () => {
      try {
        // Turbo's fetchResponse may expose the original Response at .response
        const resp = fetchResponse && (fetchResponse.response || fetchResponse)
        if (resp && typeof resp.json === 'function') {
          return await resp.json()
        }
      } catch (e) {
        // fallthrough
      }
      return null
    }

    if (success) {
      const json = await extractJson()
      if (json && (json.message || json.data)) {
        const msg = json.message || (json.data && json.data.message) || '¡Gracias! Te hemos agregado a la lista de espera.'
        this.showMessage(String(msg), 'success')
      } else {
        this.showMessage('¡Gracias! Te hemos agregado a la lista de espera.', 'success')
      }

      this.resetForm()

      // Keep the form visible to show the success message for a few seconds, then collapse
      setTimeout(() => {
        if (this.formTarget) this.formTarget.classList.add('hidden')
        if (this.primaryButtonTarget) this.primaryButtonTarget.classList.remove('hidden')
        if (this.secondaryButtonTarget) this.secondaryButtonTarget.classList.remove('hidden')
        if (this.learnMoreTarget) this.learnMoreTarget.classList.add('hidden')
        this.clearMessages() // Clear the message after hiding
      }, 3000)

      // return focus to email input for keyboard users
      if (this.emailInputTarget) setTimeout(() => this.emailInputTarget.focus(), 50)
    } else {
      const json = await extractJson()
      if (json) {
        if (json.error || json.errors) {
          const err = json.error || (Array.isArray(json.errors) ? json.errors.join(', ') : json.errors)
          this.showMessage(String(err), 'error')
        }
      } else if (fetchResponse && fetchResponse.response) {
        // Handle cases where no JSON but HTTP response exists
        if (fetchResponse.response.status === 422) {
          this.showMessage('Error de validación. Revisa los campos.', 'error')
        } else {
          this.showMessage('Error inesperado. Inténtalo de nuevo.', 'error')
        }
      } else {
        this.showMessage('Error de conexión. Inténtalo de nuevo.', 'error')
      }
    }
  }

  private clearMessages() {
    if (this.messagesTarget) this.messagesTarget.innerHTML = ''
  }

  private showMessage(message: string, type: 'success' | 'error') {
    if (this.messagesTarget) {
      const className = type === 'success' ? 'text-green-600 font-bold text-lg bg-green-50 p-3 rounded-md border border-green-200' : 'text-red-400'
      this.messagesTarget.innerHTML = `<p class="${className}">${message}</p>`
    }
  }

  private resetForm() {
    if (this.waitlistFormTarget) this.waitlistFormTarget.reset()
  }
}